import React from 'react';
import Leadership from '../components/Leadership';
import { Button } from '../components/ui/button';
import { ArrowRight, Award, Globe, Target, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold">
                  About GaaS Trust
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Decades of Experience,
                <br />
                <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                  One Powerful Platform
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Built by international business leaders with 70+ years of combined expertise, 
                our platform transforms cultural complexity into systematic competitive advantage 
                for enterprises operating across EMEA and APAC markets.
              </p>
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-xl group"
              >
                Partner With Us
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Award className="w-8 h-8 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">70+</p>
                    <p className="text-sm text-gray-600">Years Combined Expertise</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To empower international enterprises with systematic cultural intelligence and governance 
                excellence, transforming the 70% failure rate of cross-border ventures into proven success 
                through primary source networks and hybrid AI-human validation.
              </p>
              <div className="space-y-3">
                {[
                  "Deliver 90%+ accuracy vs 65% industry standard",
                  "Provide real-time intelligence across 47 jurisdictions",
                  "Enable systematic competitive advantage",
                  "Ensure regulatory compliance excellence"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center">
                <Globe className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To become the global standard for governance intelligence and cultural competency, 
                enabling businesses to thrive in any market through unparalleled network access, 
                expert validation, and cutting-edge technology.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                {[
                  { value: "47", label: "Jurisdictions" },
                  { value: "90%+", label: "Accuracy Target" },
                  { value: "24/7", label: "Intelligence" },
                  { value: "70+", label: "Years Expertise" }
                ].map((stat, index) => (
                  <div key={index} className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-xl">
                    <p className="text-3xl font-bold text-indigo-600">{stat.value}</p>
                    <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <Leadership />

      {/* Our Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our work and relationships
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "🎯",
                title: "Accuracy First",
                description: "90%+ accuracy through primary source validation and expert human-in-loop processes",
                image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80"
              },
              {
                icon: "🤝",
                title: "Trust & Transparency",
                description: "Building lasting partnerships through ethical practices and open communication",
                image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&q=80"
              },
              {
                icon: "🌍",
                title: "Global Excellence",
                description: "Unreplicable network access across 47 jurisdictions in EMEA & APAC",
                image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=400&q=80"
              },
              {
                icon: "🚀",
                title: "Innovation",
                description: "Hybrid AI-human approach delivering real-time intelligence with cultural context",
                image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80"
              }
            ].map((value, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-32 overflow-hidden">
                  <img 
                    src={value.image} 
                    alt={value.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 space-y-3">
                  <div className="text-4xl">{value.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Join Leading Enterprises Worldwide
          </h2>
          <p className="text-xl text-indigo-100">
            Partner with us to transform cultural complexity into competitive advantage
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/contact')}
            className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-6 text-lg transition-all duration-200 hover:shadow-xl group"
          >
            Start Your Partnership
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;


